// frontend/src/components/book/BookDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import MessagesView from '../shared/MessagesView';
import RulesView from '../shared/RulesView';
import WeeklyFiguresSection from '../PerformanceTab';
import PendingSection from './PendingSection';
import GameAdminSection from './GameAdminSection';
import CustomerAdminSection from './CustomerAdminSection';
import AgentAdminSection from './AgentAdminSection';
import CreateAccountSection from './CreateAccountSection';
import BookSettingsSection from './BookSettingsSection';
import FaqSection from './FaqSection';
import FeedbackSection from './FeedbackSection';
import BillingSection from './BillingSection';

const SECTIONS = [
  { key: 'weekly_figures', label: 'Weekly Figures', enabled: true },
  { key: 'pending', label: 'Pending', enabled: true },
  { key: 'game_admin', label: 'Game Admin', enabled: true },
  { key: 'customer_admin', label: 'Customer Admin', enabled: true },
  { key: 'agent_admin', label: 'Agent Admin', enabled: true },
  { key: 'create_account', label: 'Create Account', enabled: true },
  { key: 'billing', label: 'Billing', enabled: true },
  { key: 'settings', label: 'Settings', enabled: true },
  { key: 'rules', label: 'Rules', enabled: true },
  { key: 'feedback', label: 'Feedback', enabled: true },
  { key: 'faq', label: 'F.A.Q', enabled: false },
  { key: 'user_manual', label: 'User Manual', enabled: false },
];

export default function BookDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [showMessages, setShowMessages] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const checkUnread = async () => {
    try {
      const data = await api.getUnreadCount();
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error('Failed to get unread count:', err);
    }
  };

  useEffect(() => {
    (async () => {
      const data = await api.getDashboard();
      setStats(data.stats);
    })();
    checkUnread();
  }, []);

  const balanceTile = (label, value, color) => (
    <div style={{ background: colors.cardBg, boxShadow: colors.shadowCard, borderRadius: 8, padding: '1rem' }}>
      <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginBottom: 4 }}>{label}</p>
      <p style={{ color, fontSize: '1.4rem', fontWeight: 700 }}>{value}</p>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case 'weekly_figures': return <WeeklyFiguresSection user={user} />;
      case 'pending': return <PendingSection />;
      case 'game_admin': return <GameAdminSection />;
      case 'customer_admin': return <CustomerAdminSection currentUser={user} />;
      case 'agent_admin': return <AgentAdminSection currentUser={user} />;
      case 'create_account': return <CreateAccountSection user={user} />;
      case 'billing': return <BillingSection />;
      case 'settings': return <BookSettingsSection user={user} />;
      case 'rules': return <RulesView />;
      case 'feedback': return <FeedbackSection />;
      case 'faq': return <FaqSection />;
      default: return null;
    }
  };

  if (showMessages) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bgGradient, fontFamily, padding: '1rem' }}>
        <button onClick={() => { setShowMessages(false); checkUnread(); }} style={{ marginBottom: '1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.5rem 1rem', cursor: 'pointer', fontFamily }}>
          ← Back
        </button>
        <MessagesView user={user} onUpdateUnread={checkUnread} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.bgGradient, fontFamily, padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h1 style={{ color: colors.textPrimary, margin: 0 }}>{user.name}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={() => setShowMessages(true)} style={{ position: 'relative', background: colors.cardBg, border: 'none', borderRadius: 8, padding: '0.6rem', cursor: 'pointer', color: colors.textPrimary }}>
            <Mail size={20} />
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: -4, right: -4, background: colors.negative, color: '#fff', fontSize: '0.65rem', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {unreadCount}
              </span>
            )}
          </button>
          <button onClick={onLogout} style={{ background: colors.cardBg, border: 'none', borderRadius: 8, padding: '0.6rem 1rem', cursor: 'pointer', color: colors.textPrimary, fontFamily }}>
            Log Out
          </button>
        </div>
      </div>

      {activeSection ? (
        <div>
          <button onClick={() => setActiveSection(null)} style={{ marginBottom: '1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.5rem 1rem', cursor: 'pointer', fontFamily }}>
            ← Back to Dashboard
          </button>
          {renderSection()}
        </div>
      ) : (
        <>
          {stats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {balanceTile('My Balance', `$${stats.my_balance.toFixed(2)}`, stats.my_balance === 0 ? colors.odds : colors.negative)}
              {balanceTile('Weekly Balance', `$${stats.weekly_balance.toFixed(2)}`, stats.weekly_balance >= 0 ? colors.odds : colors.negative)}
              {balanceTile('Today Balance', `$${stats.today_balance.toFixed(2)}`, stats.today_balance >= 0 ? colors.odds : colors.negative)}
              {balanceTile('Active Accounts', stats.active_accounts, colors.textPrimary)}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
            {SECTIONS.map(s => (
              <button
                key={s.key}
                disabled={!s.enabled}
                onClick={() => s.enabled && setActiveSection(s.key)}
                style={{
                  padding: '1.25rem 1rem', borderRadius: 10, border: 'none', cursor: s.enabled ? 'pointer' : 'default',
                  background: s.enabled ? colors.cardBg : 'rgba(255,255,255,0.02)',
                  boxShadow: s.enabled ? colors.shadowCard : 'none',
                  color: s.enabled ? colors.textPrimary : colors.textFaint,
                  fontFamily, fontSize: '0.95rem', fontWeight: 600, textAlign: 'center'
                }}
              >
                {s.label}
                {!s.enabled && <div style={{ fontSize: '0.7rem', color: colors.textFaint, marginTop: 4, fontWeight: 400 }}>Coming Soon</div>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
